import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, ExternalLink, Facebook, Linkedin, Link2, Sparkles, Gift, Share2, Ticket } from "lucide-react";
import { toast } from "sonner";
import { ChampionAvatar } from "./ChampionAvatar";
import { getRankStyle, ordinal } from "../lib/rankStyle";
import { celebrateRank } from "../lib/celebrate";

// X (formerly Twitter) brand logo — lucide-react no longer ships this icon.
const XIcon = ({ size = 16, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const fmt = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const ChampionModal = ({ champion, onClose, hideRank = false }) => {
  useEffect(() => {
    if (!champion) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [champion, onClose]);

  return (
    <AnimatePresence>
      {champion && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[85] overflow-y-auto overscroll-contain bg-mds-navy/70 backdrop-blur-md"
          data-testid="champion-modal"
          data-lenis-prevent
        >
          <div
            className="flex min-h-full items-center justify-center p-4 sm:p-6"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative my-auto w-full max-w-2xl rounded-lg bg-mds-paper shadow-2xl"
            >
              <ModalBody champion={champion} onClose={onClose} hideRank={hideRank} />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ModalBody = ({ champion, onClose, hideRank = false }) => {
  const rs = getRankStyle(hideRank ? null : champion.rank);
  const goal = champion.goal || 0;
  const pct = goal > 0 ? Math.min(100, Math.round((champion.amount_raised / goal) * 100)) : 0;
  // Top-5 celebration: prominent rank-coloured confetti across the browser on open
  useEffect(() => {
    if (!hideRank && rs.badgeSvg) {
      celebrateRank(rs.color);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [champion.account_id]);

  // Share links point at the backend Open Graph endpoint so social platforms
  // (Facebook, LinkedIn, X, iMessage, etc.) unfurl a per-champion preview card.
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${champion.account_id}`;
  const shareText = `Help ${champion.first_name || champion.name}! Together, We Can Advance the Life-Changing Work of Naples Cancer Advisors.`;

  const openShare = (href) => window.open(href, "_blank", "noopener,noreferrer,width=640,height=640");

  const isTouch = () =>
    typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

  // On touch devices, try the native app's URL scheme; if the app isn't installed
  // (page stays visible), fall back to the network's web share dialog via a
  // top-level navigation (not popup-blocked like a delayed window.open would be).
  const openApp = (appUrl, webUrl) => {
    if (!isTouch()) {
      openShare(webUrl);
      return;
    }
    let handled = false;
    const cleanup = () => {
      document.removeEventListener("visibilitychange", cancel);
      window.removeEventListener("pagehide", cancel);
    };
    const cancel = () => {
      handled = true;
      clearTimeout(fallback);
      cleanup();
    };
    const fallback = setTimeout(() => {
      cleanup();
      if (!handled && !document.hidden) window.location.href = webUrl;
    }, 600);
    document.addEventListener("visibilitychange", cancel);
    window.addEventListener("pagehide", cancel);
    window.location.href = appUrl;
  };

  const shareTargets = [
    {
      key: "facebook",
      Icon: Facebook,
      label: "Share on Facebook",
      // Facebook has no share deep link; the web sharer opens the FB app via
      // universal links when it is installed.
      action: () =>
        openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`),
    },
    {
      key: "x",
      Icon: XIcon,
      label: "Share on X",
      action: () =>
        openApp(
          `twitter://post?message=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
        ),
    },
    {
      key: "linkedin",
      Icon: Linkedin,
      label: "Share on LinkedIn",
      action: () => openShare(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`),
    },
    {
      key: "copy",
      Icon: Link2,
      label: "Copy link",
      action: async () => {
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success("Support link copied", { description: `Share ${champion.first_name || champion.name}'s page anywhere.` });
        } catch {
          toast.error("Could not copy link");
        }
      },
    },
  ];

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Support ${champion.name}`, text: shareText, url: shareUrl });
      } catch {
        /* user cancelled */
      }
    }
  };

  return (
    <>
      <button
        onClick={onClose}
        className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-mds-line bg-mds-white/80 text-mds-ink backdrop-blur transition-colors hover:border-mds-gold hover:text-mds-gold"
        aria-label="Close"
        data-testid="champion-modal-close"
      >
        <X size={17} />
      </button>

      <div className="flex flex-col items-center px-7 pt-10 text-center sm:px-10">
        <div className="relative">
          <ChampionAvatar champion={champion} ringStyle={rs.ringStyle} sizeClass="h-40 w-40" textClass="text-4xl" />
          {!hideRank && rs.badgeSvg && (
            <img
              src={rs.badgeSvg}
              alt={`${ordinal(champion.rank)} place`}
              className="pointer-events-none absolute -bottom-1 -right-1 h-16 w-16 drop-shadow-[0_2px_5px_rgba(0,0,0,0.3)]"
              data-testid="champion-modal-medal"
            />
          )}
        </div>
        <h3 className="mt-4 font-display smallcaps text-3xl leading-tight text-mds-ink" data-testid="champion-modal-name">
          {champion.name}
        </h3>
        <p className="mt-1.5 font-body text-sm italic text-mds-inksoft">Together, We Can Advance the Life-Changing Work of Naples Cancer Advisors</p>
      </div>

      {/* Raised / goal */}
      <div className="mx-7 mt-6 rounded-md border border-mds-line bg-mds-white px-6 py-5 sm:mx-10">
        <div className="flex items-end justify-between">
          <div>
            <p className="subhead text-[10px]">Raised</p>
            <p className="font-display text-3xl text-mds-ink">{fmt(champion.amount_raised)}</p>
          </div>
          <div className="text-right">
            <p className="subhead text-[10px]">Goal</p>
            <p className="font-display text-xl text-mds-inksoft">{goal ? fmt(goal) : "—"}</p>
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-mds-line">
          <div
            className="h-full rounded-full"
            style={{ width: `${pct}%`, background: "linear-gradient(90deg,#B08A2E,#C9A24C)" }}
          />
        </div>
        <p className="mt-1.5 text-right font-sans text-[10px] uppercase tracking-[0.14em] text-mds-inksoft">
          {pct}% of goal
        </p>
      </div>

      {/* Share (above About) */}
      <div className="px-7 pt-6 sm:px-10">
        <p className="subhead text-[10px]">Share {champion.first_name || "this champion"}'s page</p>
        <div className="mt-2.5 flex items-center gap-2.5" data-testid="champion-share-row">
          {shareTargets.map(({ key, Icon, label, action }) => (
            <button
              key={key}
              onClick={action}
              aria-label={label}
              title={label}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-mds-line bg-mds-white text-mds-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-mds-gold hover:text-mds-gold"
              data-testid={`champion-share-${key}`}
            >
              <Icon size={16} strokeWidth={1.6} />
            </button>
          ))}
          {typeof navigator !== "undefined" && navigator.share && (
            <button
              onClick={nativeShare}
              className="ml-auto font-sans text-[11px] uppercase tracking-[0.14em] text-mds-gold underline-offset-4 hover:underline"
              data-testid="champion-share-native"
            >
              More…
            </button>
          )}
        </div>
      </div>

      {/* How you can make a difference */}
      <div className="px-7 pt-6 sm:px-10" data-testid="champion-help-section">
        <p className="subhead text-[10px]">How You Can Make a Difference</p>
        <ul className="mt-3 space-y-3.5">
          <li className="flex gap-3">
            <Gift size={17} strokeWidth={1.6} className="mt-0.5 shrink-0 text-mds-gold" />
            <p className="font-body text-sm leading-relaxed text-mds-ink/85">
              <span className="font-semibold text-mds-ink">Give.</span> Every gift makes a difference and brings us closer to the green.
            </p>
          </li>
          <li className="flex gap-3">
            <Share2 size={17} strokeWidth={1.6} className="mt-0.5 shrink-0 text-mds-gold" />
            <p className="font-body text-sm leading-relaxed text-mds-ink/85">
              <span className="font-semibold text-mds-ink">Share My Page.</span> Sharing with friends, family, and your social networks extends the reach of our mission farther than I ever could alone.
            </p>
          </li>
          <li className="flex gap-3">
            <Ticket size={17} strokeWidth={1.6} className="mt-0.5 shrink-0 text-mds-gold" />
            <p className="font-body text-sm leading-relaxed text-mds-ink/85">
              <span className="font-semibold text-mds-ink">Join Me at Million Dollar Shot.</span> Purchase a table or ticket and join me at the Million Dollar Shot gala.
            </p>
          </li>
        </ul>
        <p className="mt-4 font-body text-sm italic leading-relaxed text-mds-inksoft">
          Thank you for standing with me. Your support means more than you know!
        </p>
      </div>

      <div className="px-7 pt-6 sm:px-10">
        <p className="subhead text-[10px]">About {champion.first_name || "this champion"}</p>
        <p className="mt-2 font-body text-sm leading-relaxed text-mds-ink/85">
          {champion.bio ||
            `${champion.first_name || champion.name} is proud to stand as a Champion for the Million Dollar Shot, rallying our community in support of Naples Cancer Advisors. Every gift fuels life-changing cancer care and brings hope to families across Southwest Florida.`}
        </p>
      </div>

      {/* CTA */}
      <div className="mt-6 space-y-2.5 rounded-b-lg border-t border-mds-line bg-mds-paper px-7 py-5 sm:px-10">
        <a
          href={champion.support_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-mds-gold px-6 py-3.5 font-sans text-[12px] font-semibold uppercase tracking-[0.16em] text-mds-cream transition-colors duration-300 hover:bg-mds-greendeep"
          data-testid="champion-support-button"
        >
          <Heart size={14} /> Help Me Support Naples Cancer Advisors
          <ExternalLink size={13} className="opacity-70" />
        </a>
        <a
          href="#event"
          onClick={onClose}
          className="flex w-full items-center justify-center gap-2 rounded-full border border-mds-gold/50 px-6 py-3 font-sans text-[12px] font-semibold uppercase tracking-[0.16em] text-mds-ink transition-colors duration-300 hover:border-mds-gold hover:text-mds-gold"
          data-testid="champion-join-button"
        >
          <Sparkles size={14} className="text-mds-gold" /> Join Me at Million Dollar Shot
        </a>
      </div>
    </>
  );
};
